import { useAtomValue } from "jotai";
import { useMemo, useState, useEffect } from "react";

import { getMetadataFieldInfo } from "../../../../utils/RecordUtils";
import { getRecordImageInfo } from "../../../../utils/RecordUtils";
import { Field, Module } from "../../../../types/TenantInterfaces";
import { modulesAtom } from "../../../../atoms/GlobalAtoms";
import FieldUtils from "../../../../utils/FieldUtils";
import TenantAPI from "../../../../api/api/TenantAPI";

export interface AvailableModuleData {
  Module: Module;
  MetadataField: string | null;
  MetadataFieldType: string | null;
  SubitemSchema: Field | undefined;
  ImageField: string | null;
  ImagePresentation: string | null;
}

/**
 * Generates a modules map based on a sources list (Currently used for Single/Multi References)
 * Record Name Metadata Info & Record Name Image Info are included in the map as well.
 * Provide tenantSourceID in order to get a module from a specific tenant. (Used in Feedback Transform Drawer)
 */
const useAvailableModules = (sourcesList: Array<string> | string) => {
  const isPublicForm = window.location.pathname.startsWith("/forms");
  const currentTenantModules = useAtomValue(modulesAtom);

  const [mods, setMods] = useState<Module[] | null>(null);

  useEffect(() => {
    const fetchModules = async () => {
      if (isPublicForm) return setMods([]);
      if (
        (Array.isArray(sourcesList) && sourcesList[0].startsWith("Tenant.")) ||
        (Array.isArray(sourcesList) && sourcesList[0].startsWith("Modules")) ||
        (Array.isArray(sourcesList) && sourcesList[0].startsWith("Settings")) ||
        (typeof sourcesList === "string" &&
          (sourcesList.startsWith("Tenant.") ||
            sourcesList.startsWith("Modules") ||
            sourcesList.startsWith("Settings")))
      ) {
        setMods(currentTenantModules);
      } else {
        const stringValue =
          typeof sourcesList === "string" ? sourcesList : sourcesList[0];
        const stringArray = stringValue.split(".");
        const tenantID = stringArray[0];
        const moduleName = stringArray[2];

        const latestSchema = await TenantAPI.getLatestSchema(tenantID);

        const foundModule = latestSchema?.CRM.Modules.find(
          (mod: any) => mod.APIName === moduleName
        );

        setMods(foundModule ? [foundModule] : []);
      }
    };

    fetchModules();
  }, [currentTenantModules, sourcesList]);

  const availableModulesAny: Map<string, AvailableModuleData> = useMemo(() => {
    const modulesMap = new Map();

    if (!sourcesList || !mods) return modulesMap;
    if (sourcesList !== "Any") return modulesMap;

    for (const module of mods) {
      const [metadata, fieldType] = getMetadataFieldInfo(module);

      modulesMap.set(module.APIName, {
        Module: module,
        MetadataField: metadata,
        MetadataFieldType: fieldType,
      });
    }

    return modulesMap;
  }, [sourcesList, mods]);

  const availableModules: Map<string, AvailableModuleData> = useMemo(() => {
    const modulesMap = new Map();

    if (!sourcesList || !mods) return modulesMap;
    if (sourcesList === "Any") return modulesMap;

    const sources = Array.isArray(sourcesList) ? sourcesList : [sourcesList];

    for (const sourceString of sources) {
      const foundModule = FieldUtils.getSourceModuleForField(
        mods,
        sourceString
      );

      if (!foundModule?.Fields) {
        const splitString = sourceString.split(".");

        // if (splitString.length < 4) continue;

        const foundModule = mods.find((mod) => mod.APIName === splitString[2]);

        if (!foundModule?.Fields) continue;

        const subitemSchema = foundModule.Fields.find(
          (f) => f.APIName === splitString[3]
        );
        const [metadata, metadataFieldType] = getMetadataFieldInfo(foundModule);
        const [imageField, presentation] = getRecordImageInfo(foundModule);

        modulesMap.set(`${splitString[2]}.${splitString[3]}`, {
          Module: foundModule,
          SubitemSchema: subitemSchema,
          MetadataField: metadata,
          MetadataFieldType: metadataFieldType,
          ImageField: imageField,
          ImagePresentation: presentation,
        });

        continue;
      }

      const [metadata, metadataFieldType] = getMetadataFieldInfo(foundModule);
      const [imageField, presentation] = getRecordImageInfo(foundModule);

      modulesMap.set(foundModule.APIName, {
        Module: foundModule,
        MetadataField: metadata,
        MetadataFieldType: metadataFieldType,
        ImageField: imageField,
        ImagePresentation: presentation,
      });
    }

    return modulesMap;
  }, [sourcesList, mods]);

  return {
    loading: !mods,
    modulesMap: sourcesList === "Any" ? availableModulesAny : availableModules,
  };
};

export default useAvailableModules;
